/**
 * Built-in {@link AuthTokenStore} implementations.
 */

import type { AuthTokenStore, AuthTokens } from "./types";

/**
 * Web default. The server owns the `animas_auth` / `animas_refresh` cookies, so
 * the client holds no token: `read()` is null and requests go out with
 * `credentials: "include"`.
 */
export function createCookieTokenStore(): AuthTokenStore {
  return {
    credentials: "include",
    read: () => null,
    write: () => undefined,
    clear: () => undefined,
  };
}

/**
 * In-memory token-mode store (tests, or apps that must not persist tokens).
 * Requests carry `Authorization: Bearer` and no cookies.
 */
export function createMemoryTokenStore(initial?: AuthTokens): AuthTokenStore {
  let tokens: AuthTokens | null = initial ?? null;
  return {
    credentials: "omit",
    read: () => tokens?.accessToken ?? null,
    readRefreshToken: () => tokens?.refreshToken ?? null,
    write: (next) => {
      // A refresh response may omit the refresh token (no rotation) - keep the old one.
      tokens = { accessToken: next.accessToken, refreshToken: next.refreshToken ?? tokens?.refreshToken };
    },
    clear: () => {
      tokens = null;
    },
  };
}
